import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { ChangeEvent, FormEvent, memo, useState } from 'react';
import { CategoryWithRatings } from '../../../types/category-with-ratings';
import { RatingSentence } from '../../../types/rating-sentence';
import { Gender, Student } from '../../../types/student';
import StudentRatingPicker, {
  StudentRatingPickerProps,
} from './StudentRatingPicker';
import { getGenderedRatingText } from '../../../helpers/ratings';

const StudentRatingPickerMemo = memo<StudentRatingPickerProps>(
  StudentRatingPicker,
  (newProps, oldProps) =>
    newProps.newSelectedRatingIds.length ===
    oldProps.newSelectedRatingIds.length
);

export interface StudentRatingEditorDialogProps {
  onClose: () => void;
  onConfirm: (newRating: string, selectedCategoryIDs: string[]) => void;
  categoriesWithRatings: CategoryWithRatings[];
  student: Student;
  subjectName: string;
  selectedRatingIds: string[];
  textRating?: string;
}

const StudentRatingEditorDialog: React.FC<StudentRatingEditorDialogProps> = ({
  onClose,
  onConfirm,
  categoriesWithRatings,
  student,
  subjectName,
  selectedRatingIds,
  textRating,
}) => {
  const [newTextRating, setNewTextRating] = useState<string>(textRating ?? '');
  const [newSelectedRatingIds, setNewSelectedRatingIds] =
    useState<string[]>(selectedRatingIds);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!newTextRating.trim() || newTextRating === textRating) return onClose();

    onConfirm(newTextRating.trim(), newSelectedRatingIds);
  };

  const appendRatingToText = (rating: RatingSentence) => {
    const text = getGenderedRatingText(rating, student.gender);

    setNewTextRating((prevTextRating) => {
      if (
        prevTextRating.length === 0 ||
        prevTextRating.endsWith(' ') ||
        prevTextRating.endsWith('\n')
      ) {
        prevTextRating += `${text}`;
      } else {
        prevTextRating += ` ${text}`;
      }

      if (!text.endsWith('.')) prevTextRating += '.';

      return prevTextRating;
    });
  };

  const removeRatingFromText = (rating: RatingSentence) => {
    let text = getGenderedRatingText(rating, student.gender);

    setNewTextRating((prevTextRating) => {
      if (!text.endsWith('.')) text += '.';

      if (prevTextRating.startsWith(`${text} `)) {
        prevTextRating = prevTextRating.replace(`${text} `, '');
      } else if (prevTextRating.includes(` ${text}`)) {
        prevTextRating = prevTextRating.replace(` ${text}`, '');
      } else if (prevTextRating.includes(text)) {
        prevTextRating = prevTextRating.replace(text, '');
      }

      return prevTextRating;
    });
  };

  const clearTextAndSelectedRatingIds = () => {
    setNewTextRating('');
    setNewSelectedRatingIds([]);
  };

  const toggleRating = (rating: RatingSentence) => {
    const tmpSelectedRatingIds = [...newSelectedRatingIds];
    const id = rating.id!.toString();

    if (tmpSelectedRatingIds.includes(id)) {
      tmpSelectedRatingIds.splice(tmpSelectedRatingIds.indexOf(id), 1);
      removeRatingFromText(rating);
    } else {
      tmpSelectedRatingIds.push(id);
      appendRatingToText(rating);
    }

    setNewSelectedRatingIds(tmpSelectedRatingIds);
  };

  const getDialogTitle = () =>
    `${textRating ? 'Upravit' : 'Vytvořit nové'} hodnocení ${
      student.gender === Gender.Male ? 'žáka' : 'žákyně'
    } ${student.name} pro předmět ${subjectName}`;

  return (
    <Dialog
      open
      fullWidth
      disableRestoreFocus
      onClose={onClose}
      maxWidth="lg"
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
      }}
    >
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      <DialogContent>
        <Box display="flex">
          <StudentRatingPickerMemo
            categoriesWithRatings={categoriesWithRatings}
            newSelectedRatingIds={newSelectedRatingIds}
            toggleRating={toggleRating}
            student={student}
          />
          <Box width={16} />
          <TextField
            autoFocus
            multiline
            rows={22}
            fullWidth
            required
            value={newTextRating}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setNewTextRating(event.target.value)
            }
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={clearTextAndSelectedRatingIds}>Vyčistit</Button>
        <Box>
          <Button onClick={onClose} sx={{ mr: 1 }}>
            Zrušit
          </Button>
          <Button variant="contained" type="submit">
            {textRating ? 'Uložit' : 'Vytvořit'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default StudentRatingEditorDialog;
